import React, {useEffect, useState, useRef} from 'react';
import './ArrPagination.scss';
import {Select, MenuItem, Typography} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {pagination_page_limit} from '../../../shared/constants';
import {t} from 'i18next';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ArrButton from '../arr-button/ArrButton';
import {useDispatch, useSelector} from 'react-redux';
import {RootState, AppDispatch} from '../../../redux/store';
import {
  setPaginationFocus,
  clearPaginationFocus,
} from '../../../redux/slice/uiSlice';
import type {GAPaginationClickInfo} from '../../../utils/analyticsUtils';

export interface PaginationProps {
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  perPage: number;
  onPerPageChange: (perPage: number) => void;
  mode?: 'wide' | 'limited';
  perPageSize?: number[];
  entityType?: string;
  showPerPageOptions?: boolean;
  perPageOptions?: number[] | undefined;
  alwaysShow?: boolean;
  isFetching?: boolean;
  showInfoText?: boolean;
  perPageLabel?: string;
  onPaginationClick?: (info: GAPaginationClickInfo) => void;
}

const ArrPagination: React.FC<PaginationProps> = ({
  totalItems,
  currentPage,
  onPageChange,
  perPage,
  onPerPageChange, // eslint-disable-next-line @typescript-eslint/no-unused-vars
  perPageSize,
  mode = 'wide',
  entityType = 'users',
  showPerPageOptions = false,
  perPageOptions = [25, 50, 100],
  alwaysShow = false,
  isFetching,
  showInfoText = true,
  perPageLabel = 'Per page',
  onPaginationClick,
}) => {
  const totalPages = Math.ceil(totalItems / perPage);
  const perPageAnchorRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const paginationFocusPage = useSelector(
    (state: RootState) => state.ui?.paginationFocusPage
  );
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalItems);

  const [selectOpen, setSelectOpen] = useState(false);
  useEffect(() => {
    if (isFetching) return;
    const maxPage = Math.max(1, Math.ceil(totalItems / perPage));
    if (currentPage > maxPage) {
      onPageChange(maxPage);
    }
  }, [totalItems, perPage, currentPage, onPageChange]);

  if (!alwaysShow && totalItems < pagination_page_limit) return null;

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];

    const maxVisible = mode === 'wide' ? 8 : 5;
    const siblingCount = Math.floor((maxVisible - 3) / 2);

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const leftSibling = Math.max(currentPage - siblingCount, 2);
      const rightSibling = Math.min(currentPage + siblingCount, totalPages - 1);
      pages.push(1);

      // Left ellipsis
      if (leftSibling > 2) {
        pages.push('...');
      }

      // Middle pages
      for (let i = leftSibling; i <= rightSibling; i++) {
        pages.push(i);
      }

      // Right ellipsis
      if (rightSibling < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = generatePageNumbers();
  useEffect(() => {
    if (!pages || pages.length === 0) return;
    if (typeof paginationFocusPage !== 'number') return;
    if (paginationFocusPage !== currentPage) return;

    requestAnimationFrame(() => {
      const selector = `[data-testid="page-${currentPage}-btn"]`;
      const wrapper = document.querySelector(selector) as HTMLElement | null;
      if (!wrapper) return;
      const btn = (wrapper.querySelector('button') as HTMLElement) || wrapper;
      try {
        btn.focus({preventScroll: false});
      } catch {
        // ignore
      }
    });

    // clear flag in redux store so we don't refocus repeatedly
    dispatch(clearPaginationFocus());
  }, [currentPage, pages, paginationFocusPage, dispatch]);
  const NoIcon = React.memo(() => null);
  return (
    <div
      className={`arr-pagination ${mode}`}
      data-testid={`arr-pagination-pages`}
    >
      {/* Info Section */}
      <div
        className="pagination-info-wrapper"
        data-testid={`arr-pagination-div`}
      >
        {mode === 'wide' && showInfoText && (
          <Typography
            className="pagination-info typo-text body3_size"
            data-testid="pagination-info"
            tabIndex={0}
          >
            Showing {startItem} to {endItem} of {totalItems} {entityType}
          </Typography>
        )}
      </div>
      {/* Controls Section */}
      <div className="pagination-controls" data-testid={'pagination-controls'}>
        {showPerPageOptions && (
          <div className="per-page-control" data-testid="per-page-control">
            <div
              className="per-page-select-wrapper"
              ref={perPageAnchorRef}
              data-testid={`arr-pagination-div-page-select-dropdown`}
            >
              <Select
                className="per-page-select"
                open={selectOpen}
                onOpen={() => setSelectOpen(true)}
                onClose={() => setSelectOpen(false)}
                value={perPage}
                onChange={(e) => {
                  const nextPerPage = Number(e.target.value);
                  onPaginationClick?.({
                    control: 'perPage',
                    perPage: nextPerPage,
                  });
                  onPerPageChange(nextPerPage);
                  onPageChange(1);
                }}
                size="small"
                variant="outlined"
                IconComponent={NoIcon}
                SelectDisplayProps={{
                  'data-testid': `per-page-control-dropdown`,
                }}
                renderValue={(value) => (
                  <div
                    className="select-render-value"
                    data-testid={`arr-pagination-`}
                  >
                    <span
                      className="select-label"
                      data-testid={`arr-pagination--text`}
                    >
                      {perPageLabel}: {value}
                    </span>
                    {selectOpen ? (
                      <ArrowDropUpIcon className="select-caret" />
                    ) : (
                      <ArrowDropDownIcon className="select-caret" />
                    )}
                  </div>
                )}
                MenuProps={{
                  anchorEl: perPageAnchorRef.current,
                  anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                  transformOrigin: {vertical: 'top', horizontal: 'left'},
                  PaperProps: {
                    className: 'per-page-dropdown custom-dropdown-menu',
                  },
                }}
              >
                {perPageOptions?.map((opt) => (
                  <MenuItem
                    className="custom-menu-item"
                    key={opt}
                    value={opt}
                    data-testid={`per-page-${opt}`}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        )}

        {/* Pages Label */}
        <div className="pages-label" data-testid={`arr-pagination-pages-btn`}>
          <Typography
            className="typo-text body3_size label"
            data-testid={`arr-pagination-pages-text`}
          >
            {t('common.arrPagination.pages')}
          </Typography>
        </div>

        {/* Page Buttons */}
        <div
          className="pagination-buttons"
          data-testid={`arr-pagination-div-button`}
        >
          <div
            className="custom-pagination"
            data-testid={`arr-pagination-pages-wrapper`}
          >
            {pages.map((page, idx) =>
              page === '...' ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="ellipsis"
                  data-testid={`arr-pagination-ellipsis-${idx}`}
                >
                  ...
                </span>
              ) : (
                <ArrButton
                  key={page}
                  className={` page-button ${page === currentPage ? 'active' : ''} typo-text buttonSmall_size `}
                  onClick={() => {
                    dispatch(setPaginationFocus(Number(page)));
                    onPaginationClick?.({
                      control: 'page',
                      page: Number(page),
                    });
                    onPageChange(Number(page));
                  }}
                  dataTestId={`page-${page}-btn`}
                  aria-label={`page-${page}`}
                >
                  {page}
                </ArrButton>
              )
            )}
            <ArrIconButton
              onClick={() => {
                dispatch(setPaginationFocus(currentPage - 1));
                onPaginationClick?.({control: 'arrow', direction: 'left'});
                onPageChange(currentPage - 1);
              }}
              disabled={currentPage === 1}
              className="nav-button"
              ariaLabel={t('common.arrPagination.previousPage')}
              size="small"
              testId="previous-page-btn"
              icon={<NavigateBeforeIcon />}
            />
            <ArrIconButton
              onClick={() => {
                dispatch(setPaginationFocus(currentPage + 1));
                onPaginationClick?.({control: 'arrow', direction: 'right'});
                onPageChange(currentPage + 1);
              }}
              disabled={currentPage === totalPages}
              className="nav-button"
              ariaLabel={t('common.arrPagination.nextPage')}
              size="small"
              testId="next-page-btn"
              icon={<NavigateNextIcon />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArrPagination;
